import { err, ok, type Result } from '@shared/result';

/**
 * A date on a calendar — a year, a month and a day, with no time and no zone.
 *
 * Why a value object instead of `Date`: every date in this system is a calendar
 * date, not an instant. "Vacaciones del 10 al 21 de agosto" means the same days
 * whether the reader is in Mexico City or Madrid. A JavaScript `Date` is an
 * instant, and mixing the two is the classic source of off-by-one-day bugs —
 * `new Date('2026-08-10')` parses as UTC midnight, which is 9 August in
 * Mexico. That single line would shift every vacation range by a day and
 * miscount business days near a weekend.
 *
 * All arithmetic here goes through an epoch-day integer derived with `Date.UTC`,
 * so the host timezone cannot influence any result. `Date` never escapes this
 * module.
 */

export type CalendarDateError =
  | { readonly kind: 'malformed'; readonly raw: string }
  | { readonly kind: 'out-of-range'; readonly raw: string };

const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

/** Sunday-first, matching `Date.prototype.getUTCDay` and the design's `DOWS`. */
export const enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export class CalendarDate {
  private constructor(
    readonly year: number,
    /** 1-12, not the 0-11 of `Date`. Off-by-one months are not a thing here. */
    readonly month: number,
    readonly day: number,
  ) {}

  /**
   * Parses `YYYY-MM-DD`. Rejects anything else, including a valid-looking date
   * that does not exist (`2026-02-30`) — the round-trip check below catches
   * those, where a naive `new Date(...)` would silently roll over to 2 March.
   */
  static fromISO(raw: string): Result<CalendarDate, CalendarDateError> {
    const match = ISO_PATTERN.exec(raw);
    if (match === null) return err({ kind: 'malformed', raw });

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return err({ kind: 'out-of-range', raw });
    }

    // Reject dates that do not exist by checking the components survive a
    // round trip through the calendar.
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (
      probe.getUTCFullYear() !== year ||
      probe.getUTCMonth() !== month - 1 ||
      probe.getUTCDate() !== day
    ) {
      return err({ kind: 'out-of-range', raw });
    }

    return ok(new CalendarDate(year, month, day));
  }

  /** For literals known to be valid — seed data and test fixtures. */
  static fromISOOrThrow(raw: string): CalendarDate {
    const parsed = CalendarDate.fromISO(raw);
    if (!parsed.ok) throw new Error(`Not a calendar date: "${raw}" (${parsed.error.kind})`);
    return parsed.value;
  }

  static of(year: number, month: number, day: number): Result<CalendarDate, CalendarDateError> {
    const pad = (n: number, width: number): string => String(n).padStart(width, '0');
    return CalendarDate.fromISO(`${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`);
  }

  /**
   * The calendar date an instant falls on, in the *host* timezone. This is the
   * one place a zone legitimately matters: "today" for a person in Mexico City
   * is a local question. Everything downstream is zone-free again.
   */
  static fromInstantLocal(instant: Date): CalendarDate {
    return new CalendarDate(instant.getFullYear(), instant.getMonth() + 1, instant.getDate());
  }

  /** Days since the Unix epoch. The basis of all arithmetic below. */
  get epochDay(): number {
    return Math.round(Date.UTC(this.year, this.month - 1, this.day) / MS_PER_DAY);
  }

  private static fromEpochDay(epochDay: number): CalendarDate {
    const d = new Date(epochDay * MS_PER_DAY);
    return new CalendarDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }

  toISO(): string {
    const pad = (n: number, width: number): string => String(n).padStart(width, '0');
    return `${pad(this.year, 4)}-${pad(this.month, 2)}-${pad(this.day, 2)}`;
  }

  toString(): string {
    return this.toISO();
  }

  /** Serialises as its ISO string, so a range inside JSON stays readable. */
  toJSON(): string {
    return this.toISO();
  }

  addDays(count: number): CalendarDate {
    return CalendarDate.fromEpochDay(this.epochDay + count);
  }

  addMonths(count: number): CalendarDate {
    const zeroBased = this.month - 1 + count;
    const year = this.year + Math.floor(zeroBased / 12);
    const month = ((zeroBased % 12) + 12) % 12;
    // Clamp rather than roll over: one month after 31 January is 28 February,
    // not 3 March. Month navigation in the calendar, and the balance expiry —
    // where a rolled-over day would move the date somebody is counting down to.
    const lastDay = CalendarDate.daysInMonth(year, month + 1);
    return new CalendarDate(year, month + 1, Math.min(this.day, lastDay));
  }

  addYears(count: number): CalendarDate {
    const year = this.year + count;
    const lastDay = CalendarDate.daysInMonth(year, this.month);
    return new CalendarDate(year, this.month, Math.min(this.day, lastDay));
  }

  static daysInMonth(year: number, month: number): number {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  }

  get dayOfWeek(): DayOfWeek {
    return new Date(this.epochDay * MS_PER_DAY).getUTCDay();
  }

  /**
   * Saturday and Sunday. Note this is *not* the same question as "is a business
   * day" — that also depends on the holiday calendar and lives in
   * `BusinessDayPolicy`, because the holiday list is runtime configuration.
   */
  get isWeekend(): boolean {
    return this.dayOfWeek === DayOfWeek.Saturday || this.dayOfWeek === DayOfWeek.Sunday;
  }

  /** First day of this date's month. */
  get startOfMonth(): CalendarDate {
    return new CalendarDate(this.year, this.month, 1);
  }

  /** Last day of this date's month. */
  get endOfMonth(): CalendarDate {
    return new CalendarDate(this.year, this.month, CalendarDate.daysInMonth(this.year, this.month));
  }

  /**
   * Monday of this date's week.
   *
   * Monday and not Sunday because that is the week this company reads: the
   * calendar grid starts on Monday and so does the leader's «esta semana». The
   * arithmetic goes through `dayOfWeek`, which is `getUTCDay` — Sunday is 0, so
   * the offset is `(day + 6) % 7` rather than `day - 1`, which would send Sunday
   * six days forward instead of six days back.
   */
  get startOfWeek(): CalendarDate {
    return this.addDays(-((this.dayOfWeek + 6) % 7));
  }

  /** Sunday of this date's week. */
  get endOfWeek(): CalendarDate {
    return this.startOfWeek.addDays(6);
  }

  /**
   * Este mismo día si ya es el día de la semana pedido, y si no el siguiente que
   * lo sea. Nunca mira hacia atrás, así que la distancia va de 0 a 6 días.
   *
   * Es lo que permite decir «hasta el viernes» sin que la frase se rompa en fin de
   * semana: un sábado devuelve el viernes que viene, no el que acaba de pasar, y
   * la ventana sigue midiendo como mucho una semana.
   */
  onOrNext(day: DayOfWeek): CalendarDate {
    return this.addDays((day - this.dayOfWeek + 7) % 7);
  }

  /** Negative if before, zero if equal, positive if after. */
  compare(other: CalendarDate): number {
    return this.epochDay - other.epochDay;
  }

  equals(other: CalendarDate): boolean {
    return this.epochDay === other.epochDay;
  }

  isBefore(other: CalendarDate): boolean {
    return this.epochDay < other.epochDay;
  }

  isAfter(other: CalendarDate): boolean {
    return this.epochDay > other.epochDay;
  }

  isSameOrBefore(other: CalendarDate): boolean {
    return this.epochDay <= other.epochDay;
  }

  isSameOrAfter(other: CalendarDate): boolean {
    return this.epochDay >= other.epochDay;
  }

  /** Whole days from this date to `other`; negative if `other` is earlier. */
  daysUntil(other: CalendarDate): number {
    return other.epochDay - this.epochDay;
  }

  static min(a: CalendarDate, b: CalendarDate): CalendarDate {
    return a.isBefore(b) ? a : b;
  }

  static max(a: CalendarDate, b: CalendarDate): CalendarDate {
    return a.isAfter(b) ? a : b;
  }

  /**
   * Ascending comparator for `Array.prototype.sort`.
   *
   * An arrow property rather than a static method so it can be passed unbound —
   * `dates.sort(CalendarDate.ascending)` — without picking up a `this` binding.
   */
  static readonly ascending = (a: CalendarDate, b: CalendarDate): number => a.compare(b);
}
